import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Skeleton,
  useMediaQuery,
  Grid, // Import Grid component
  Alert,
} from "@mui/material";
import Swal from "sweetalert2";
import { customerservice, reportsManagementUrl } from "../../../../../config";
import { generateReport } from "../../../../../data/DynamicTable/DynamicFunctions";
import {
  fetchCustomerStatementJsonDynamic,
  fetchJsonDynamic,
} from "../../../../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../../../../theme";

// Role groups that are always allowed to see the statement report section,
// regardless of the "Hide Customer Statement for FO" setting. Any
// RoleGroupId NOT in this list is treated as an FO-equivalent role and is
// subject to the hide setting. Kept in sync with CustomerStatementRight.
const ALWAYS_ALLOWED_ROLE_GROUP_IDS = [1, 2, 3, 4];

const CustomerStatementDetails = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [runTour, setRunTour] = useState(() => {
    const stored = localStorage.getItem("TourCompleted");
    return stored !== "true";
  });
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const customerId = localStorage.getItem("CustomerId");

  // ── FO statement-access gating ────────────────────────────────────────
  // "Hide Customer Statement for FO" comes from customerSettings. When it's
  // true AND the logged-in user's RoleGroupId is NOT one of the always-
  // allowed role groups, the "Generate Customer Statement" section is
  // hidden.
  const [hideStatementForFO, setHideStatementForFO] = useState(false);
  const [isCustomerSettingsLoaded, setIsCustomerSettingsLoaded] =
    useState(false);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const roleGroupId = parseInt(decodedToken.RoleGroupId);
  const isRestrictedRole = !ALWAYS_ALLOWED_ROLE_GROUP_IDS.includes(
    roleGroupId
  );

  // Should the "Generate Customer Statement" section be shown?
  const canAccessStatementReport = !(isRestrictedRole && hideStatementForFO);

  // Joyride steps
  const steps = [
    {
      target: "#customer-details-section",
      content: "This section displays the customer's loan and savings details.",
    },
    ...(canAccessStatementReport
      ? [
          {
            target: "#report-section",
            content:
              "Use this section to generate the customer's loan and savings report.",
          },
          {
            target: "#generate-report-button",
            content: "Click here to generate and view the customer's statement.",
          },
        ]
      : []),
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const variables = { customerid: parseInt(customerId) };
        const response = await fetchCustomerStatementJsonDynamic(
          "customerLoanAndSavingsById",
          "customer",
          variables,
          customerservice.uri
        );
        setData(JSON.parse(response.results));
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [customerId]);

  // ── Fetch customer settings (e.g. "Hide Customer Statement for FO") ────
  useEffect(() => {
    async function fetchCustomerSettings() {
      try {
        const response = await fetchJsonDynamic(
          "customerSettings",
          null,
          null,
          customerservice.uri
        );

        if (response?.results) {
          const settings = JSON.parse(response.results);

          // Trim both the setting name and value since the source data can
          // contain trailing whitespace, e.g. "Hide Customer Statement for FO ".
          const hideStatementSetting = settings.find(
            (s) => s.Setting?.trim() === "Hide Customer Statement for FO"
          );

          setHideStatementForFO(
            hideStatementSetting?.Value?.trim()?.toLowerCase() === "true"
          );
        }
      } catch (error) {
        console.error("Error fetching customer settings:", error);
        setHideStatementForFO(false);
      } finally {
        setIsCustomerSettingsLoaded(true);
      }
    }
    fetchCustomerSettings();
  }, []);

  const handleGenerateReport = async () => {
    if (!canAccessStatementReport) return;

    const reportTypeId = 42;
    const reportType = "Customer Statement";

    try {
      setIsLoading(true);
      const mutationData = {
        ReportTypeId: reportTypeId,
        ReportType: reportType,
        CustomerId: parseInt(customerId),
        StartDate: startDate,
        EndDate: endDate,
      };

      const newTab = window.open("", "_blank");
      newTab.document.write("<p>Loading...</p>");

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLoading
      );

      if (response.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        newTab.close();
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to generate the report. Please try again later.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while generating the report.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const toLocaleString = (value) =>
  Number(value ?? 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const fields = [
    {
      label: "LoanID",
      value: `${data?.loanid || "N/A"}`,
    },
    { label: "First Name", value: data?.first_name || "N/A" },
    { label: "Last Name", value: data?.last_name || "N/A" },
    { label: "Mobile Number", value: data?.mobile_number || "N/A" },
    {
      label: "Date Created",
      value: data?.date_created
        ? new Date(data?.date_created).toLocaleDateString()
        : "N/A",
    },
    { label: "Loan Amount", value: `KSH ${toLocaleString(data?.active_loan ?? 0.0)}` },
    { label: "OLB", value: `KSH ${toLocaleString(data?.olb ?? 0.0)}` },
    { label: "Arrears", value: `KSH ${toLocaleString(data?.loan_arrears ?? 0.0)}` },
    { label: "Days Overdue", value: data?.days_overdue || 0 },
    { label: "Serviced Loans", value: data?.serviced_loans || 0},
    {
      label: "BBF Account Balance",
      value: `KSH ${toLocaleString(data?.bbf_account_balance ?? 0.0)}`,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: { xs: 1, sm: 2 },
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
       <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Button onClick={() => navigate(-1)} variant="contained">
                Back to Customers
              </Button>
              <Button onClick={() => setRunTour(true)} variant="outlined" color="primary">
                Start Page Tour
              </Button>
            </Box>
        {/* Joyride tour */}
      <Joyride
        steps={steps}
        run={runTour}
        showSkipButton
        continuous
        scrollToFirstStep
        styles={{
          options: {
            primaryColor: theme.palette.primary.main,
            zIndex: 10000,
          },
        }}
        callback={(data) => {
          const { status } = data;
          const finishedStatuses = ["finished", "skipped"];
          if (finishedStatuses.includes(status)) {
            localStorage.setItem("TourCompleted", "true");
            setRunTour(false);
          }
        }}
      />

      <Box id="customer-details-section">
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            textAlign: { xs: "center", sm: "left" },
            mb: 3,
            fontSize: "1rem",
          }}
        >
          Customer Details
        </Typography>
        {isSmallScreen ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              background: colors.primary[200],
              borderRadius: 2,
              padding: 2,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            }}
          >
            {fields.map((field, index) => (
              <Grid
                container
                key={index}
                sx={{
                  borderBottom:
                    index !== fields.length - 1 ? "1px solid #ccc" : "none", // Add border except for the last item
                  py: 1, // Add padding to separate rows
                }}
              >
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {field.label}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {field.value}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              background: `${colors.primary[200]} !important`,
              borderRadius: 2,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {fields.map((field, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        background: colors.primary[400],
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                      }}
                    >
                      {field.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    {fields.map((_, index) => (
                      <TableCell key={index}>
                        <Skeleton variant="text" height={40} />
                      </TableCell>
                    ))}
                  </TableRow>
                ) : (
                  <TableRow>
                    {fields.map((field, index) => (
                      <TableCell key={index} sx={{ fontSize: "0.73rem" }}>
                        {field.value}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Generate Customer Statement: hidden for any RoleGroupId not in the
          always-allowed list when customerSettings' "Hide Customer
          Statement for FO" is true. */}
      {!isCustomerSettingsLoaded ? null : canAccessStatementReport ? (
        <Box id="report-section">
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              textAlign: { xs: "center", sm: "left" },
              mb: 2,
              fontSize: "1rem",
            }}
          >
            Generate Customer Statement
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              background: colors.primary[200],
              borderRadius: 2,
              padding: { xs: 1.5, sm: 2 },
              maxWidth: "300px",
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              sx={{ background: colors.primary[400], borderRadius: 1 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              sx={{ background: colors.primary[400], borderRadius: 1 }}
            />
            <Button
              id="generate-report-button"
              variant="contained"
              color="primary"
              onClick={handleGenerateReport}
              disabled={isLoading}
              fullWidth
              size="small"
              sx={{
                background: colors.blueAccent[500],
                "&:hover": { background: colors.blueAccent[600] },
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Generate Report"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Alert severity="info" sx={{ maxWidth: 300 }}>
          You don't have permission to generate customer statements.
        </Alert>
      )}
    </Box>
  );
};

export default CustomerStatementDetails;